import { useGetOne, useRecordContext, useTranslate } from "ra-core";
import { useMemo, useRef } from "react";
import { Lock } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Deal, Task } from "../types";
import { useTaskMeeting } from "./useTaskMeeting";
import {
  meetingClockRef,
  playableRecordings,
  RecordingPlayer,
  useRecordings,
} from "../zoom_meeting_summaries/RecordingPlayer";
import { TimestampBadge } from "../zoom_meeting_summaries/TimestampBadge";
import { TranscriptPanel } from "../zoom_meeting_summaries/TranscriptPanel";
import { useSeekWhenReady } from "../zoom_meeting_summaries/useSeekWhenReady";
import {
  type MeetingClock,
  meetingSpan,
  mergedCues,
  toMeetingTime,
} from "../zoom_meeting_summaries/meetingTimeline";

/**
 * When a task was created from a Zoom meeting (`zoom_meeting_id` set), show the
 * meeting recording + transcript inline in the task modal, auto-seeked to the
 * moment the task came up. Renders nothing for non-Zoom tasks.
 */
export const TaskMeetingPlayback = () => {
  const task = useRecordContext<Task>();
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const videoRef = useRef<MeetingClock>(null);
  const meetingId = task?.zoom_meeting_id as number | undefined;
  // The deal lives on the source meeting, not the task — derive it. Fetched
  // before the recordings because its meeting_end_time bounds their polling.
  const { meeting, restricted } = useTaskMeeting(meetingId);
  const { data: recordings, isPending } = useRecordings(
    meetingId,
    meeting ? meetingClockRef(meeting) : null,
  );
  const dealId = meeting?.deal_id ?? undefined;
  const { data: deal } = useGetOne<Deal>(
    "deals",
    { id: dealId! },
    { enabled: !!dealId },
  );

  // The meeting from start to end with every recorded part in place, and every
  // part's transcript on that meeting clock. The task's offset is stored in
  // recording time, so it moves onto the clock by the span's leadIn.
  const span = useMemo(
    () => meetingSpan(meeting, recordings),
    [meeting, recordings],
  );
  const cues = useMemo(() => mergedCues(span.parts, span.leadIn), [span]);
  const hasPlayable = playableRecordings(recordings).length > 0;

  const offset = task?.offset_seconds;
  const moment = offset == null ? null : toMeetingTime(offset, span.leadIn);

  // Seek the recording to the task's moment once the player has mounted and its
  // active track has metadata (RecordingPlayer assigns videoRef async). Waits for
  // the meeting row, which sets where the moment falls.
  useSeekWhenReady(videoRef, moment, hasPlayable && !!meeting, span);

  if (!meetingId) return null;

  // A confidential meeting the reader is not on the list for: say so, rather
  // than promise a recording that will never load for them. LCCRM-93.
  if (restricted) {
    return (
      <div className="flex flex-col gap-1 border-t pt-3">
        <span className="flex items-center gap-1 text-sm font-medium text-muted-foreground">
          <Lock className="size-3.5" />
          {translate("resources.tasks.restricted_meeting.heading")}
        </span>
        <span className="text-sm text-muted-foreground">
          {translate("resources.tasks.restricted_meeting.no_access")}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 border-t pt-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            From Zoom meeting
          </span>
          {hasPlayable && offset != null && (
            <TimestampBadge
              offset={offset}
              leadIn={span.leadIn}
              videoRef={videoRef}
            />
          )}
        </div>
        {/* The mobile app has no meeting page to open. */}
        {meeting?.id != null && !isMobile && (
          <a
            href={`#/zoom_meetings/${meeting.id}/show`}
            className="text-xs text-primary hover:underline"
          >
            Open full meeting →
          </a>
        )}
      </div>
      {deal && (
        <div className="text-sm">
          <span className="text-muted-foreground">Deal: </span>
          {deal.name}
        </div>
      )}
      {isPending ? (
        <span className="text-sm text-muted-foreground">
          Loading recording…
        </span>
      ) : hasPlayable ? (
        <div className="flex flex-col gap-2">
          <div className="h-56">
            <RecordingPlayer
              meetingRef={meetingId}
              meetingEndTime={meeting ? meetingClockRef(meeting) : null}
              meeting={meeting}
              videoRef={videoRef}
            />
          </div>
          {cues.length > 0 && (
            <div className="h-48">
              <TranscriptPanel
                videoRef={videoRef}
                cues={cues}
                highlightTime={moment ?? undefined}
              />
            </div>
          )}
        </div>
      ) : (
        <span className="text-sm text-muted-foreground">
          Recording isn't ready yet.
        </span>
      )}
    </div>
  );
};
